import {
  getEquivalentRevitParameterNames,
  sanitizeRevitParameterNames,
} from '../../../../shared/lib/revitParameterNames';
import {
  areDataTypesCompatible,
  isKnownDataType,
} from '../../../../shared/lib/revit/familyParameterTargetSelection';
import { collectAdditionalCategoryTargets } from '../../../../shared/lib/hooks/useParameterAddition';
import { sanitizeMappingParameterReferenceName } from '../mappingParameterReferenceName';
import type { FormField } from '../../../../shared/types';
import {
  collectBoltConfiguredParameters,
  getFormFieldValue,
  normalizeLookupName,
  type MappingParameterRepairKind,
  type MappingProjectParameterReference,
  type MappingProjectReadinessInput,
} from './mappedParameterReferences';

export interface MappingProjectCatalogInput {
  projectParameterNames?: string[];
  sampledParameterNames?: string[];
}

export interface MappingProjectCatalogDescriptor {
  name: string;
  dataType?: string;
}

export interface MappingProjectParameterNameGroups {
  missing: string[];
  blocked: string[];
  repairable: string[];
}

export interface MappingProjectReadinessDiff {
  references: MappingProjectParameterReference[];
  missing: MappingProjectParameterReference[];
  repairable: MappingProjectParameterReference[];
  blocked: MappingProjectParameterReference[];
}

interface MappingSharedParameterDefinition {
  name: string;
  dataType: string;
}

export function buildDefinitionBackedMappingCatalogDiffs(
  references: MappingProjectParameterReference[],
  catalog: MappingProjectCatalogInput,
  catalogDescriptors: MappingProjectCatalogDescriptor[],
  sharedParameterDefinitions: MappingSharedParameterDefinition[]
): {
  catalogDiff: MappingProjectReadinessDiff;
  readinessDiff: MappingProjectReadinessDiff;
} {
  const sharedParameterNames = sharedParameterDefinitions.map((parameter) => parameter.name);
  const catalogDiff = promoteSharedFileRepairableParameters(
    diffMappedParametersAgainstCatalog(references, catalog),
    sharedParameterNames
  );
  const readinessDiff = demoteTypeMismatchedCatalogParameters(
    catalogDiff,
    catalogDescriptors,
    new Map(sharedParameterDefinitions.map((parameter) => [
      parameter.name.trim().toLowerCase(),
      parameter.dataType,
    ])),
    sharedParameterNames
  );

  return { catalogDiff, readinessDiff };
}

/**
 * Moves references that ARE present by name but carry an incompatible data
 * type into the missing set. A name-only check treats a TEXT binding of an
 * INTEGER-mapped parameter as present, so a profile swap would never repair
 * it. References whose expected type is unknown (no shared-file definition)
 * and descriptors without a data type are left alone — no guessing.
 * Mismatches keep their repair kinds (or inherit 'mapping' when the shared
 * file backs the name, the same promotion the missing path uses).
 */
export function demoteTypeMismatchedCatalogParameters(
  diff: MappingProjectReadinessDiff,
  catalogDescriptors: MappingProjectCatalogDescriptor[],
  expectedTypesByName: ReadonlyMap<string, string>,
  sharedParameterNames: Array<string | null | undefined>
): MappingProjectReadinessDiff {
  if (catalogDescriptors.length === 0 || expectedTypesByName.size === 0) {
    return diff;
  }

  // Expanding every row into alias keys lets an earlier equivalent mask a
  // later exact descriptor; actual-name keys preserve exact-first resolution.
  const descriptorLookup = new Map<string, MappingProjectCatalogDescriptor>();
  catalogDescriptors.forEach((descriptor) => {
    const key = descriptor.name.trim().toLowerCase();
    if (!descriptorLookup.has(key)) {
      descriptorLookup.set(key, descriptor);
    }
  });
  const sharedLookup = createParameterLookup(sharedParameterNames);
  const alreadyMissing = new Set(diff.missing.map((reference) => normalizeLookupName(reference.name)));

  const findExpectedType = (name: string): string | undefined =>
    getEquivalentRevitParameterNames(name)
      .map((parameterName) => expectedTypesByName.get(parameterName.trim().toLowerCase()))
      .find((expected): expected is string => Boolean(expected));

  const findDescriptor = (name: string): MappingProjectCatalogDescriptor | undefined =>
    getEquivalentRevitParameterNames(name)
      .map((parameterName) => descriptorLookup.get(parameterName.trim().toLowerCase()))
      .find((candidate): candidate is MappingProjectCatalogDescriptor => Boolean(candidate));

  const isTypeMismatch = (reference: MappingProjectParameterReference): boolean => {
    if (alreadyMissing.has(normalizeLookupName(reference.name))) {
      return false;
    }

    const expectedType = findExpectedType(reference.name);
    if (!expectedType) {
      return false;
    }

    const descriptor = findDescriptor(reference.name);
    return Boolean(
      descriptor?.dataType &&
      isKnownDataType(descriptor.dataType) &&
      !areDataTypesCompatible(descriptor.dataType, expectedType)
    );
  };

  const demoted = diff.references.map((reference): MappingProjectParameterReference => {
    if (!isTypeMismatch(reference)) {
      return reference;
    }

    if (reference.repairKinds.length > 0) {
      return reference;
    }

    const sharedFileBacked = getEquivalentRevitParameterNames(reference.name).some(
      (parameterName) => sharedLookup.has(normalizeLookupName(parameterName))
    );

    return sharedFileBacked ? { ...reference, repairKinds: ['mapping'] } : reference;
  });

  const demotedMissing = demoted.filter((reference, index) =>
    isTypeMismatch(diff.references[index])
  );
  if (demotedMissing.length === 0) {
    return diff;
  }

  const missing = diff.missing.concat(demotedMissing);

  return {
    references: demoted,
    missing,
    repairable: missing.filter((reference) => reference.repairKinds.length > 0),
    blocked: missing.filter((reference) => reference.repairKinds.length === 0),
  };
}

export function createParameterLookup(parameterNames: Array<string | null | undefined>): Set<string> {
  const lookup = new Set<string>();

  parameterNames.forEach((parameterName) => {
    const sanitized = sanitizeMappingParameterReferenceName(parameterName);
    if (sanitized) {
      getEquivalentRevitParameterNames(sanitized).forEach((parameterName) => {
        lookup.add(normalizeLookupName(parameterName));
      });
    }
  });

  return lookup;
}

export function diffMappedParametersAgainstCatalog(
  references: MappingProjectParameterReference[],
  catalog: MappingProjectCatalogInput
): MappingProjectReadinessDiff {
  const available = createParameterLookup([
    ...(catalog.projectParameterNames ?? []),
    ...(catalog.sampledParameterNames ?? []),
  ]);

  const missing = references.filter(
    (reference) =>
      !getEquivalentRevitParameterNames(reference.name).some((parameterName) =>
        available.has(normalizeLookupName(parameterName))
      )
  );
  const repairable = missing.filter((reference) => reference.repairKinds.length > 0);
  const blocked = missing.filter((reference) => reference.repairKinds.length === 0);

  return {
    references,
    missing,
    repairable,
    blocked,
  };
}

/**
 * Missing mapped parameters that exist in the loaded shared-parameter file
 * are not hard blockers: the repair pass can bind them to the project with
 * the selected wizard binding mode straight from the file definition.
 * Names absent from the file stay blocked — the wizard must not fabricate
 * parameters it knows nothing about.
 */
export function promoteSharedFileRepairableParameters(
  diff: MappingProjectReadinessDiff,
  sharedParameterNames: Array<string | null | undefined>
): MappingProjectReadinessDiff {
  const sharedLookup = createParameterLookup(sharedParameterNames);

  const promote = (
    reference: MappingProjectParameterReference
  ): MappingProjectParameterReference => {
    if (reference.repairKinds.length > 0) {
      return reference;
    }

    const isSharedFileParameter = getEquivalentRevitParameterNames(reference.name).some(
      (parameterName) => sharedLookup.has(normalizeLookupName(parameterName))
    );

    return isSharedFileParameter
      ? { ...reference, repairKinds: ['mapping'] }
      : reference;
  };

  const references = diff.references.map(promote);
  const missing = diff.missing.map(promote);
  const repairable = missing.filter((reference) => reference.repairKinds.length > 0);
  const blocked = missing.filter((reference) => reference.repairKinds.length === 0);

  return {
    references,
    missing,
    repairable,
    blocked,
  };
}

export function getMappingReadinessRepairKinds(
  diff: MappingProjectReadinessDiff
): MappingParameterRepairKind[] {
  return Array.from(new Set(diff.repairable.flatMap((reference) => reference.repairKinds))).sort();
}

/**
 * Bolt/additional setup actions (and the classification Quick Fill pass) ride
 * along on a repair only while the diff itself implicates a real setup area
 * (connector, bolt, additional, process/project). A diff that only lacks
 * shared-file mapped parameters — the steady state right after the wizard
 * bound everything else — would otherwise turn a sub-second binding step
 * into a minute of family-document churn. The mapping-only path still runs
 * the authoritative final Quick Fill after its new bindings are available.
 */
export function resolveMappingReadinessRepairKinds(input: {
  repairableCount: number;
  diffRepairKinds: MappingParameterRepairKind[];
  selectedSetupRepairKinds: MappingParameterRepairKind[];
}): MappingParameterRepairKind[] {
  if (input.repairableCount === 0 || input.diffRepairKinds.every((kind) => kind === 'mapping')) {
    return [...input.diffRepairKinds];
  }

  return Array.from(new Set([...input.diffRepairKinds, ...input.selectedSetupRepairKinds]));
}

export function getMappingReadinessNameGroups(
  diff: MappingProjectReadinessDiff
): MappingProjectParameterNameGroups {
  return {
    missing: diff.missing.map((reference) => reference.name),
    blocked: diff.blocked.map((reference) => reference.name),
    repairable: diff.repairable.map((reference) => reference.name),
  };
}

export function getMappingReadinessSelectedSetupRepairKinds(
  input: MappingProjectReadinessInput
): MappingParameterRepairKind[] {
  const kinds: MappingParameterRepairKind[] = [];

  if (sanitizeRevitParameterNames(collectBoltConfiguredParameters(input.boltConfig)).length > 0) {
    kinds.push('bolt');
  }

  if (
    collectAdditionalCategoryTargets(input.additionalCategoriesConfig)
      .some((target) => target.parameterNames.length > 0)
  ) {
    kinds.push('additional');
  }

  return kinds;
}

function pluralizeFamilyGroup(label: string): string {
  switch (label) {
    case 'BOLT':
      return 'Bolts';
    case 'GASKET':
      return 'Gaskets';
    case 'VALVE':
      return 'Valves';
    case 'SUPPORT':
    case 'SUPPORTS':
      return 'Supports';
    case 'INSTRUMENT':
      return 'Instruments';
    default:
      return label;
  }
}

export function getMappingReadinessFamilyRepairGroups(
  input: MappingProjectReadinessInput,
  diff: MappingProjectReadinessDiff
): string[] {
  const groups = new Set<string>();
  const missingLookup = createParameterLookup(diff.missing.map((reference) => reference.name));
  const hasRepairKind = (kind: MappingParameterRepairKind): boolean =>
    diff.repairable.some((reference) => reference.repairKinds.includes(kind));

  if (hasRepairKind('bolt')) {
    groups.add(pluralizeFamilyGroup(input.boltConfig.boltModelingChoice?.value === 'yes' ? 'BOLT' : 'GASKET'));
  }

  const addAdditionalGroup = (field: FormField | string | null | undefined, label: string) => {
    const parameterName = sanitizeMappingParameterReferenceName(getFormFieldValue(field));
    if (
      parameterName &&
      getEquivalentRevitParameterNames(parameterName).some((candidate) =>
        missingLookup.has(normalizeLookupName(candidate))
      )
    ) {
      groups.add(label);
    }
  };

  addAdditionalGroup(input.additionalCategoriesConfig.valveTagValue, 'Valves');
  addAdditionalGroup(input.additionalCategoriesConfig.supportsTagValue, 'Supports');
  addAdditionalGroup(input.additionalCategoriesConfig.supportsSsrefValue, 'Supports');
  addAdditionalGroup(input.additionalCategoriesConfig.instrumentTagValue, 'Instruments');

  if (groups.size === 0 && hasRepairKind('additional')) {
    groups.add('Additional-category families');
  }

  return Array.from(groups).sort((left, right) => left.localeCompare(right));
}
